'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { acquireScopedSupabaseClient, releaseScopedSupabaseClient } from '@/lib/realtime';
import {
  APPROVAL_PENDING_KEY,
  PAYMENT_DEADLINE_KEY,
  isPaymentWindowExpired,
} from '@/lib/pickup-approval';

// Doc 32, build step 5: a realtime client scoped to the guest's order token.
// Clients are shared per token and reference-counted (acquire/release), so the
// status hook and the delivery hook on the same order page multiplex over a
// single connection instead of opening one socket each.

// Realtime is best-effort: if the scoped client is unavailable or the channel
// drops silently after mount, this is the safety net that keeps the tracking
// page from freezing on a stale status. It is slower than realtime (so events
// still feel instant) but fast enough that a dropped channel never reads as
// "broken".
const POLL_INTERVAL_ACTIVE_MS = 60_000;
const POLL_INTERVAL_INACTIVE_MS = 30_000;
/**
 * While the counter is deciding, or while a payment window is running down
 * (docs/operational-ux-2026-09/07-customer-ordering.md §5).
 *
 * The cadences above are for an order that is simply being cooked, where a
 * minute of staleness costs nothing. A held order is different: the guest is
 * watching the screen for a verdict, and the window they then have to pay in is
 * five minutes long. A missed realtime event there does not read as "slightly
 * behind", it reads as "they never answered" — and it can cost the order. The
 * faster loop only runs while that is true, which is minutes, not a service.
 */
const POLL_INTERVAL_AWAITING_MS = 5_000;

export type CustomerOrderStatus = {
  id: string;
  orderNumber: string;
  /**
   * The ticket code the counter calls out and prints (doc 04 §2), e.g. "P042".
   * Null when the order has no ticket yet — the surface then falls back to its
   * own neutral placeholder rather than showing the random `orderNumber`.
   */
  ticketCode: string | null;
  /**
   * The coarse guest journey the server already mapped (`received · preparing ·
   * ready · on_the_way · done · cancelled`), shipped so a surface need not
   * re-read the raw fulfilment/delivery enums.
   *
   * NOTE for the web status view: it keeps its own stage model deliberately —
   * `guestStage` flattens `awaiting_payment` (an open bill after the food is
   * served) and `served`-but-unpaid into `done`, which would wrongly close a
   * dine-in order that still owes payment. Mobile, whose rail has no
   * served/payment nuance, is the surface that renders this directly.
   */
  guestStage: string | null;
  status: string;
  paymentStatus: string;
  subtotal: number;
  taxTotal: number | null;
  serviceChargeTotal: number | null;
  deliveryFee: number | null;
  discountTotal: number | null;
  total: number;
  currency: string | null;
  itemsCount: number;
  itemSummary: Array<{ title: string; quantity: number }>;
  branchName: string | null;
  tableLabel: string | null;
  serviceMode: string | null;
  // v2 ordering dimensions (doc v2 §2-8)
  orderChannel: string | null;
  orderIntent: string | null;
  orderContext: string | null;
  orderTiming: string | null;
  deliveryStatus: string | null;
  deliveryAddress: string | null;
  eta: {
    visible: boolean;
    label: string;
    estimatedReadyAt: string | null;
    remainingMinutesRange: [number, number] | null;
    confidence: 'low' | 'medium' | 'high';
  } | null;
  /** The guest's own copy of the bill, once one has been issued. */
  receipt: { id: string; receiptNumber: string } | null;
  /**
   * The approval keys, when this order went through the counter's verdict
   * (doc 07 §5). Null on an instant-mode branch — which is every branch that
   * has not opted in — so a caller can tell "not this flow" from "held".
   */
  pickupApproval: Record<string, unknown> | null;
  /**
   * The collection code the counter asks for at hand-over, and whether it has
   * been checked yet. Null on an order that never had one (dine-in, delivery).
   *
   * Served here so the code outlives the "order placed" dialog — that dialog
   * was the only place it had ever been shown, so dismissing it lost the code
   * for good.
   */
  pickupClaim: { code: string; status: string | null } | null;
  /** What cancelling right now would mean (full refund vs. merchant review vs. not offered). */
  cancellation: {
    allowed: boolean;
    mode: 'auto' | 'merchant_review' | 'none';
    message: string;
    refundableAmount: number;
    paid: boolean;
    pending: boolean;
  } | null;
  updatedAt: string;
};

export function useCustomerOrderStatus(orderId: string, accessToken: string) {
  const [order, setOrder] = useState<CustomerOrderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // RES-1.3: last time a status fetch succeeded (initial, poll, or realtime
  // refetch). The page renders a stale marker when this ages past the
  // threshold while the order is still in flight — a dropped connection must
  // never leave a live-looking "updates on its own" page.
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const clientRef = useRef<SupabaseClient | null>(null);
  const realtimeActiveRef = useRef(false);
  /** True while a verdict or a payment window is outstanding — see the constant. */
  const approvalPendingRef = useRef(false);
  const cancelledRef = useRef(false);

  const applyOrder = useCallback((row: Record<string, unknown>) => {
    if (cancelledRef.current) return;
    setOrder({
      id: String(row.id),
      orderNumber: String(row.orderNumber),
      ticketCode: (row.ticketCode as string | null) ?? null,
      guestStage: (row.guestStage as string | null) ?? null,
      status: String(row.status),
      paymentStatus: String(row.paymentStatus),
      subtotal: Number(row.subtotal || 0),
      taxTotal: row.taxTotal ? Number(row.taxTotal) : null,
      serviceChargeTotal: row.serviceChargeTotal ? Number(row.serviceChargeTotal) : null,
      deliveryFee: row.deliveryFee ? Number(row.deliveryFee) : null,
      discountTotal: row.discountTotal ? Number(row.discountTotal) : null,
      total: Number(row.total || 0),
      currency: (row.currency as string) ?? null,
      itemsCount: Number(row.itemsCount || 0),
      itemSummary: Array.isArray(row.itemSummary)
        ? row.itemSummary.filter((item): item is { title: string; quantity: number } =>
            Boolean(item) && typeof item === 'object' && typeof (item as Record<string, unknown>).title === 'string' && Number.isFinite(Number((item as Record<string, unknown>).quantity)),
          ).map((item) => ({ title: item.title, quantity: Number(item.quantity) }))
        : [],
      branchName: (row.branchName as string | null) ?? null,
      tableLabel: (row.tableLabel as string | null) ?? null,
      serviceMode: (row.serviceMode as string | null) ?? null,
      // v2 ordering dimensions
      orderChannel: (row.orderChannel as string | null) ?? null,
      orderIntent: (row.orderIntent as string | null) ?? null,
      orderContext: (row.orderContext as string | null) ?? null,
      orderTiming: (row.orderTiming as string | null) ?? null,
      deliveryStatus: (row.deliveryStatus as string | null) ?? null,
      deliveryAddress: (row.deliveryAddress as string | null) ?? null,
      eta: (row.eta as CustomerOrderStatus['eta']) ?? null,
      // Safe to overwrite: realtime only triggers a full `loadFromApi()`
      // refetch, so this always receives the complete status payload rather
      // than a partial postgres row.
      receipt: (row.receipt as CustomerOrderStatus['receipt']) ?? null,
      cancellation: (row.cancellation as CustomerOrderStatus['cancellation']) ?? null,
      pickupApproval: (row.pickupApproval as Record<string, unknown> | null) ?? null,
      pickupClaim: (row.pickupClaim as CustomerOrderStatus['pickupClaim']) ?? null,
      updatedAt: String(row.updatedAt),
    });
    const approval = (row.pickupApproval as Record<string, unknown> | null) ?? null;
    approvalPendingRef.current = Boolean(
      approval &&
        (approval[APPROVAL_PENDING_KEY] === true ||
          (typeof approval[PAYMENT_DEADLINE_KEY] === 'string' &&
            !isPaymentWindowExpired(approval))),
    );
    setLastUpdatedAt(Date.now());
  }, []);

  // The REST status fetch is the source of truth and must run regardless of
  // whether a realtime client is available; the subscription below is a
  // best-effort enhancement layered on top. Gating this fetch on the client
  // once left the tracking page stuck on its loading skeleton forever,
  // because the realtime client factory read env through a dynamic
  // `process.env[name]` lookup that Next.js never inlines for the browser
  // and so always returned null. That is fixed in lib/realtime.ts (static
  // member expressions), but the ordering here stays deliberate: status must
  // render even where realtime is unavailable or blocked.
  const loadFromApi = useCallback(async () => {
    try {
      const res = await fetch(`/api/public/orders/${orderId}/status`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await res.json();
      if (cancelledRef.current) return;
      if (!data.ok) {
        setError(data.error || 'Could not load order status.');
        setLoading(false);
        return;
      }
      applyOrder(data.order as Record<string, unknown>);
      setLoading(false);
    } catch {
      if (cancelledRef.current) return;
      setError('Could not load order status.');
      setLoading(false);
    }
  }, [orderId, accessToken, applyOrder]);

  useEffect(() => {
    if (!orderId || !accessToken) return;

    cancelledRef.current = false;
    void loadFromApi();

    // Polling safety net. The subscription below handles live changes; this
    // loop only exists for when realtime is unavailable (no scoped client) or
    // drops after mount, so the page keeps advancing through the status rail
    // even then. Each tick re-reads the realtime status so a working channel
    // settles into the slower cadence and a dropped one speeds back up. The
    // page's "updates on their own" copy stays truthful either way.
    let pollTimer = 0;
    const schedulePoll = () => {
      const delay = approvalPendingRef.current
        ? POLL_INTERVAL_AWAITING_MS
        : realtimeActiveRef.current
          ? POLL_INTERVAL_ACTIVE_MS
          : POLL_INTERVAL_INACTIVE_MS;
      pollTimer = window.setTimeout(() => {
        void loadFromApi();
        schedulePoll();
      }, delay);
    };
    schedulePoll();

    const client = acquireScopedSupabaseClient(accessToken);
    if (!client) {
      // No realtime client (env not inlined) , the poll loop above is the only
      // update path. Don't tear down early or the page freezes.
      return () => {
        cancelledRef.current = true;
        window.clearTimeout(pollTimer);
      };
    }
    clientRef.current = client;
    // Must run synchronously before `.subscribe()` so the JOIN frame carries
    // the guest token (the scoped client starts with a null token), and the
    // explicit `setAuth` keeps the channel's post-join auth on the guest token
    // instead of falling back to the anon key. The client is callback-free
    // (see lib/realtime.ts), so nothing later overwrites these claims.
    client.realtime.setAuth(accessToken);

    // `event: '*'` matches the POS hooks and means every change type triggers
    // a refetch; the callback refetches the full status payload, so extra
    // event types are harmless.
    const channel = client
      .channel(`customer-order-${orderId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'service_orders', filter: `id=eq.${orderId}` },
        () => void loadFromApi(),
      )
      .subscribe((status) => {
        realtimeActiveRef.current = status === 'SUBSCRIBED';
      });

    return () => {
      cancelledRef.current = true;
      window.clearTimeout(pollTimer);
      if (clientRef.current) {
        void clientRef.current.removeChannel(channel);
      }
      releaseScopedSupabaseClient(accessToken);
    };
  }, [orderId, accessToken, loadFromApi]);

  return { order, loading, error, reload: loadFromApi, lastUpdatedAt };
}
